import { useEffect, useMemo, useState } from "react";
import { api, GlossaryTerm, NewGlossaryTerm } from "../lib/api";
import { GENERATION_LANGS, languageName } from "../lib/languages";

const ALL_LANGS = "*";

const CATEGORIES = ["brand", "person", "physiology", "general"] as const;

/**
 * Settings — currently just the custom dictionary (Windhund Glossary).
 * Terms steer auto-generation: they bias the transcription (Scribe
 * keyterms) and are find-replaced into generated subtitles.
 */
export function Settings() {
  const [terms, setTerms] = useState<GlossaryTerm[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const res = await api.listGlossary();
      setTerms(res.terms);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold">Settings</h1>

      <section className="card p-6 space-y-5">
        <header>
          <h2 className="text-lg font-semibold">Custom dictionary</h2>
          <p className="text-sm text-neutral-500 mt-1">
            Terms here steer subtitle &amp; dub generation: transcription is biased toward them
            (so names and jargon are spelled correctly), and generated subtitles get them
            replaced to the enforced form. <span className="font-medium">All languages</span>{" "}
            means the term is kept verbatim everywhere — right for product names and people.
          </p>
        </header>

        <AddTermForm onAdded={load} />

        {error && <p className="text-sm text-red-600">{error}</p>}
        {loaded && terms.length === 0 && !error && (
          <p className="text-sm text-neutral-500">No terms yet.</p>
        )}
        <TermsList terms={terms} onChanged={load} />
      </section>
    </div>
  );
}

function AddTermForm({ onAdded }: { onAdded: () => void }) {
  const [term, setTerm] = useState("");
  const [language, setLanguage] = useState<string>(ALL_LANGS);
  const [translated, setTranslated] = useState("");
  const [category, setCategory] = useState<string>("general");
  const [notes, setNotes] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invariant = language === ALL_LANGS;
  const canSubmit = term.trim() && (invariant || translated.trim()) && !saving;

  async function submit() {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const body: NewGlossaryTerm = {
        term_source: term.trim(),
        language,
        term_translated: invariant ? term.trim() : translated.trim(),
        category,
        notes: notes.trim() || undefined,
        case_sensitive: caseSensitive,
      };
      await api.createGlossaryTerm(body);
      setTerm("");
      setTranslated("");
      setNotes("");
      onAdded();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border border-neutral-200 rounded-md p-4 space-y-3">
      <div className="text-sm font-medium">Add term</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <label className="label">Term</label>
          <input
            className="input"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Windhund 365"
            disabled={saving}
          />
        </div>
        <div>
          <label className="label">Applies to</label>
          <select
            className="input"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            disabled={saving}
          >
            <option value={ALL_LANGS}>All languages — keep as-is</option>
            {GENERATION_LANGS.map((code) => (
              <option key={code} value={code}>
                {languageName(code)} — {code}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">{invariant ? "Kept verbatim" : "Enforced translation"}</label>
          <input
            className="input"
            value={invariant ? term : translated}
            onChange={(e) => setTranslated(e.target.value)}
            placeholder={invariant ? "" : "workplace health management"}
            disabled={saving || invariant}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <label className="label">Category</label>
          <select
            className="input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={saving}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="label">Notes (optional)</label>
          <input
            className="input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why this term matters"
            disabled={saving}
          />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-neutral-700 cursor-pointer">
          <input
            type="checkbox"
            checked={caseSensitive}
            onChange={(e) => setCaseSensitive(e.target.checked)}
            disabled={saving}
          />
          Case-sensitive match
        </label>
        <button className="btn-primary" onClick={submit} disabled={!canSubmit}>
          {saving ? "Adding…" : "Add term"}
        </button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function TermsList({ terms, onChanged }: { terms: GlossaryTerm[]; onChanged: () => void }) {
  // Group by language: invariants first, then each target language.
  const groups = useMemo(() => {
    const map = new Map<string, GlossaryTerm[]>();
    for (const t of terms) {
      const list = map.get(t.language) ?? [];
      list.push(t);
      map.set(t.language, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) =>
      a === ALL_LANGS ? -1 : b === ALL_LANGS ? 1 : a.localeCompare(b),
    );
  }, [terms]);

  if (terms.length === 0) return null;

  return (
    <div className="space-y-5">
      {groups.map(([lang, list]) => (
        <div key={lang}>
          <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">
            {lang === ALL_LANGS ? "All languages (kept verbatim)" : `${languageName(lang)} — ${lang}`}
            <span className="ml-2 text-neutral-400">{list.length}</span>
          </div>
          <ul className="divide-y divide-neutral-200 border border-neutral-200 rounded-md">
            {list.map((t) => (
              <TermRow key={t.id} term={t} onChanged={onChanged} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function TermRow({ term, onChanged }: { term: GlossaryTerm; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invariant = term.language === ALL_LANGS;

  async function remove() {
    if (!confirm(`Delete "${term.term_source}"?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteGlossaryTerm(term.id);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <li className="px-3 py-2 flex items-center justify-between gap-3">
      <div className="min-w-0 text-sm">
        <span className="font-medium">{term.term_source}</span>
        {!invariant && (
          <>
            <span className="text-neutral-400 mx-2">→</span>
            <span>{term.term_translated}</span>
          </>
        )}
        <span className="ml-2 inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-600 align-middle">
          {term.category ?? "general"}
        </span>
        {term.case_sensitive === 0 && (
          <span className="ml-1 inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-600 align-middle">
            any case
          </span>
        )}
        {term.notes && <div className="text-xs text-neutral-500 truncate">{term.notes}</div>}
        {error && <div className="text-xs text-red-600">{error}</div>}
      </div>
      <button
        className="text-xs text-red-600 hover:underline shrink-0"
        onClick={remove}
        disabled={busy}
      >
        Delete
      </button>
    </li>
  );
}
